import {
  APIListValuesRequest,
  APIListValuesResponse,
  APIPaginatedRequest,
  APISuccessResponse,
  AssociatedVehicleTimelineRequest,
  ByVehicleAgeAgeRequest,
  CountRequest,
  ExportRequest,
  getFetcher,
  GetRequest,
  ListEventsRequest,
  Part,
  SmartGroup,
  TopContributorsAgeRequest,
} from "shared/api/api";
import client from "shared/api/axios";
import { Granularity } from "shared/api/constants";
import {
  SensorReadingsTimelineAggregation,
  SensorReadingsTimelineGrouping,
} from "shared/api/sensors/api";
import { Photo } from "shared/api/types";
import { createURL } from "shared/api/utils";
import {
  CountRequestWithVehiclesFilter,
  Vehicle,
} from "shared/api/vehicles/api";
import { MileageUnit } from "shared/types";

const SIGNAL_EVENTS_ROUTE = "signalEvents";

export interface SignalEvent {
  ID: string;
  description: string | null;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
}

export interface ListSignalEventsRequest extends APIPaginatedRequest {}

export const listSignalEventsRequestURI = (
  params: ListSignalEventsRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE]),
    params,
  });

const getSignalEventRequestURI = ({ id, ...params }: GetRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, id]),
    params,
  });

export const getSignalEvent = (args: GetRequest) =>
  getFetcher<SignalEvent>(getSignalEventRequestURI(args));

export const listSignalEventsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listSignalEventsValuesRequestURI(args));

const listSignalEventsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["signalEvents", "values", fieldName]),
    params,
  });

export const getSignalEventsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getSignalEventsExportRequestURI(args), {
    responseType: "blob",
  });

const getSignalEventsExportRequestURI = ({
  IDs,
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "export", type]),
    params,
  });

export interface SignalEventsFiltersRequest {
  signalEventOccurrencesFilter?: string;
  vehiclesFilter?: string;
  claimFilter?: string;
  repairFilter?: string;
}

export interface SignalEventsRunChartRequest extends SignalEventsFiltersRequest {
  groupBy: string | null;
  numSplits?: number;
  granularity?: Granularity;
}

export interface SignalEventByVehicleAgeFiltersRequest
  extends SignalEventsFiltersRequest, ByVehicleAgeAgeRequest {}

export interface SignalEventOccurrencesRunChartResponse {
  data: SignalEventOccurrencesRunChartItem[];
  numTotalSplits: number | null;
}

export interface SignalEventOccurrencesRunChartItem {
  date: string;
  affectedVehicles: number;
  events: number;
  groupByAttributeValue: string | null;
}

export const getSignalEventsOccurrencesRunChartRequestURI = (
  params: SignalEventsRunChartRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "runChart"]),
    params,
  });

export interface SignalEventsOccurrence {
  recordedAt: string;
  VIN: string;
  signalEventType: string;
  signalEventID: string;
  occurrenceCount: number;

  SPN: number | null;
  FMI: number | null;
  obd2Code: string | null;
  extCode: string | null;
  milLampStatus: string | null;
  redLampStatus: string | null;
  amberLampStatus: string | null;
  protectLampStatus: string | null;
  ecuFamily: string | null;
  ecuVersion: string | null;
  ecuSoftwareVersion: string | null;
  ecuPartNumber: string | null;
  ecuEditionNumber: string | null;
  ecuSupplier: string | null;
  externalURL: string | null;
  symptom: string | null;
  symptomDescription: string | null;
  status: string | null;
  currentFault: string | null;
  description: string | null;
  extTransactionID: string | null;
  newOccurrence: boolean | null;
  highPriority: boolean | null;
  batteryStatus: number | null;
  airAmbientTemp: number | null;
  fuelLevel: number | null;
  warningIndicator: boolean | null;
  dtcTestFailedPresent: boolean | null;
  dtcTestFailedCurrCycle: boolean | null;
  dtcTestFailedLastCycle: boolean | null;
  dtcConfirmed: boolean | null;
  dtcTestNotCompletedSinceLastClear: boolean | null;
  dtcTestFailedSinceLastClear: boolean | null;
  dtcTestNotCompletedThisOperation: boolean | null;
  lifecycleMode: string | null;
  unitConcernState: string | null;
  evaluationComment: string | null;
  repairFirstName: string | null;
  repairLastName: string | null;
  repairDate: string | null;
  repairCausalPart: string | null;
  repairCausalPartInformation: Part | null;
  // Added so they can be included in the schema that does keys of SignalEventsOccurrence
  "repairCausalPartInformation.description": string | null;
  repairComment: string | null;
  collectionPoint: string | null;
  concern: string | null;
  concernName: string | null;
  positionA: string | null;
  positionB: string | null;
  positionC: string | null;
  positionD: string | null;
  odometer: string | null;
  odometerUnit: string | null;
  trigger: string | null;
  totalEngineHours: number | null;
  monthsInService: number | null;
  daysInService: number | null;
  matchedClaimsCount?: number;
  inspectionItem: string | null;
  inspectionPurpose: string | null;
  ingestedAt: string | null;
  mentionedSignalEvents: string | null;
  crewCollection: string | null;
  crewRepair: string | null;
  vehicle?: Vehicle;
  smartGroups?: SmartGroup[];
  vehiclePhotos?: Photo[];
}

export const listSignalEvents = (
  args: ListEventsRequest
): Promise<APISuccessResponse<SignalEvent[]>> =>
  getFetcher(listSignalEventsRequestURI({ ...args }));

const listSignalEventsOccurrencesValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "values", fieldName]),
    params,
  });

export const listSignalEventsOccurrencesValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(
    listSignalEventsOccurrencesValuesRequestURI(args)
  );

export const listSignalEventsOccurrencesRequestURI = ({
  skipRequest,
  ...params
}: ListEventsRequest): string =>
  skipRequest
    ? ""
    : client.getUri({
        method: "GET",
        url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences"]),
        params,
      });

export const listSignalEventsOccurrencesCountRequestURI = ({
  ...params
}: CountRequestWithVehiclesFilter): string =>
  client.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "count"]),
    params,
  });

// Associated Claims
export interface SignalEventAssociatedClaim {
  groupByAttributeValue: string | null;
  IPTV: number;
  numAssociatedClaims: number;
  numAssociatedVehicles: number;
  percentClaimsWithPreceding30DaysSignalEvent: number;
  totalEventOccurrences: number;
  associationStrength: number | null;
}

export interface SignalEventsAssociatedClaimsRequest
  extends APIPaginatedRequest, SignalEventsFiltersRequest {
  groupBy: string;
  signalEventsTimeWindow?: number;
}

export const listSignalEventsAssociatedClaimsRequestURI = ({
  groupBy,
  ...otherParams
}: SignalEventsAssociatedClaimsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "associatedClaims", groupBy]),
    params: otherParams,
  });

// Associated Repairs
export interface SignalEventAssociatedRepair {
  groupByAttributeValue: string | null;
  IPTV: number;
  numAssociatedRepairs: number;
  numAssociatedVehicles: number;
  percentRepairsWithPreceding30DaysSignalEvent: number;
  totalEventOccurrences: number;
  associationStrength: number | null;
}

export interface SignalEventsAssociatedRepairsRequest
  extends APIPaginatedRequest, SignalEventsFiltersRequest {
  groupBy: string;
  signalEventsTimeWindow?: number;
}

export const listSignalEventsAssociatedRepairsRequestURI = ({
  groupBy,
  ...otherParams
}: SignalEventsAssociatedRepairsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "associatedRepairs", groupBy]),
    params: otherParams,
  });

// By Production Date
export const getSignalEventsByVehicleAgeTimelineRequestURI = (
  params: SignalEventsFiltersRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "signalEventsByVehicleAgeTimeline"]),
    params,
  });

const getSignalEventsByVehicleAgeTimelineExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      SIGNAL_EVENTS_ROUTE,
      "signalEventsByVehicleAgeTimeline",
      "export",
      type,
    ]),
    params,
  });

export const getSignalEventsByVehicleAgeExport = (args: ExportRequest) =>
  getFetcher<Blob>(getSignalEventsByVehicleAgeTimelineExportRequestURI(args), {
    responseType: "blob",
  });

export interface SignalEventsTopContributorsRequest
  extends
    APIPaginatedRequest,
    SignalEventsFiltersRequest,
    TopContributorsAgeRequest {
  groupBy: string;
  mis?: string;
}

export interface SignalEventsTopContributor {
  groupByAttributeValue: string | null;
  IPTV: number;
  numSignalEventOccurrences: number;
  numVehicles: number;
  IPTV30DayChangeRatio: number | null;
  DPTV: number;
  DPTV30DayChangeRatio: number | null;
}

export const getSignalEventsTopContributorsRequestURI = ({
  groupBy,
  ...otherParams
}: SignalEventsTopContributorsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "topContributors", groupBy]),
    params: otherParams,
  });

const getSignalEventsTopContributorsExportRequestURI = ({
  IDs,
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      SIGNAL_EVENTS_ROUTE,
      "topContributors",
      ...IDs,
      "export",
      type,
    ]),
    params,
  });

export const getSignalEventsTopContributorsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getSignalEventsTopContributorsExportRequestURI(args), {
    responseType: "blob",
  });

export interface SignalEventsAssociatedVehiclesRequest
  extends APIPaginatedRequest, SignalEventsFiltersRequest {
  mileageUnit: MileageUnit;
}

export interface SignalEventsAssociatedVehiclesRequest
  extends APIPaginatedRequest, SignalEventsFiltersRequest {
  mileageUnit: MileageUnit;
}

export const listSignalEventsAssociatedVehiclesRequestURI = ({
  ...params
}: SignalEventsAssociatedVehiclesRequest): string =>
  client.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "associatedVehicles"]),
    params,
  });

export const listSignalEventsAssociatedVehiclesCountRequestURI = ({
  ...params
}: CountRequest): string =>
  client.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "associatedVehicles", "count"]),
    params,
  });

const getAssociatedVehiclesExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "associatedVehicles", "export", type]),
    params,
  });

export const getSignalEventsAssociatedVehiclesExport = (args: ExportRequest) =>
  getFetcher<Blob>(getAssociatedVehiclesExportRequestURI(args), {
    responseType: "blob",
  });

export const listDealerValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listDealerValuesRequestURI(args));

const listDealerValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["dealers", "values", fieldName]),
    params,
  });

export interface SignalEventOccurrencesVINTimelineBucket {
  signalEventID: string;
  VIN: string;
  occurrences: number;
  date: string;
  description?: string;
}

export interface SignalEventOccurrencesVINAggregateBucket {
  signalEventID: string;
  VIN: string;
  totalOccurrences: number;
  description?: string;
  signalEventType?: string;
}

export interface ListSignalEventOccurrencesVINTimelineRequest extends APIPaginatedRequest {
  grouping?: SensorReadingsTimelineGrouping;
  aggregation?: SensorReadingsTimelineAggregation;
  skipRequest?: boolean;
}

export const listSignalEventOccurrencesVINTimelineRequestURI = ({
  skipRequest,
  ...params
}: ListSignalEventOccurrencesVINTimelineRequest): string =>
  skipRequest
    ? ""
    : client.getUri({
        method: "get",
        url: createURL(["signalEvents", "occurrences", "timeline"]),
        params,
      });

export interface ListSignalEventOccurrencesVINAggregateRequest extends APIPaginatedRequest {}

export const listSignalEventOccurrencesVINAggregateRequestURI = ({
  ...params
}: ListSignalEventOccurrencesVINAggregateRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["signalEvents", "occurrences", "aggregate"]),
    params,
  });

export const listSignalEventOccurrencesVINAggregateCountRequestURI = ({
  ...params
}: ListSignalEventOccurrencesVINAggregateRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["signalEvents", "occurrences", "aggregate", "count"]),
    params,
  });

export interface AssociatedSignalEvent {
  signalEventID: string;
  description: string;
  associationStrength: number | null;
  vehiclesWithCooccurrence: number;
  associatedVehicles: number;
  percentageAssociatedVehiclesWithBase: number;
  baseVehicles: number;
  percentageBaseVehiclesWithAssociated: number;
  cooccurrenceDays: number;
  percentageAssociatedWithBase: number;
  percentageBaseWithAssociated: number;
}

export interface ListSignalEventAssociatedSignalEventsRequest extends APIPaginatedRequest {
  signalEventOccurrencesFilter?: string;
  vehiclesFilter?: string;
  associatedSignalEventsFilter: string;
}

export const listSignalEventAssociatedSignalEventsRequestURI = ({
  associatedSignalEventsFilter,
  ...params
}: ListSignalEventAssociatedSignalEventsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      "signalEvents",
      "associatedSignalEvents",
      associatedSignalEventsFilter,
    ]),
    params,
  });

export const listAssociatedVehiclesTimelineRequestURI = (
  params: AssociatedVehicleTimelineRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([SIGNAL_EVENTS_ROUTE, "associatedVehiclesTimeline"]),
    params,
  });

// Smart Groups
export interface SignalEventSmartGroup {
  groupID: string;
  groupName: string;
  groupDescription: string;
  filteredCount: number;
  allTimeCount: number;
  filteredVehicleCount: number;
  allTimeVehicleCount: number;
  filteredFRCImpact: number;
  allTimeFRCImpact: number;
  groupExternalID: string;
  totalGroupCount?: number;
}

export const listSignalEventsSmartGroupsRequestURI = (
  params: ListSignalEventsSmartGroupsRequest
): string =>
  client.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "smartGroups"]),
    params,
  });

export interface ListSignalEventsSmartGroupsRequest extends ListEventsRequest {
  eventType?: string;
  chartDateField?: string;
  granularity?: string;
}

// --------
// Signal Events Smart Groups Export
// --------
const getSignalEventsSmartGroupsExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      SIGNAL_EVENTS_ROUTE,
      "occurrences",
      "smartGroups",
      "export",
      type,
    ]),
    params,
  });

export const getSignalEventsSmartGroupsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getSignalEventsSmartGroupsExportRequestURI(args), {
    responseType: "blob",
  });

export interface MatchSignalEventClaimsRequest {
  filter: string;
  groupBy?: string;
}

export const matchSignalEventClaimsRequestURI = (
  args: MatchSignalEventClaimsRequest
): string =>
  client.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "matchedClaims"]),
    params: args,
  });
